// Schicht-Hooks: Start/Stop/Pause + Freigabe-Workflow.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type ShiftStatus = "open" | "submitted" | "approved" | "rejected";

export interface Shift {
  id: string;
  driver_id: string;
  started_at: string;
  ended_at: string | null;
  started_lat: number | null;
  started_lng: number | null;
  ended_lat: number | null;
  ended_lng: number | null;
  break_minutes: number;
  notes: string | null;
  status: ShiftStatus;
  approved_by: string | null;
  approved_at: string | null;
  rejection_note: string | null;
  tour_id: string | null;
  created_at: string;
}

export interface ShiftWithJoins extends Shift {
  driver?: { full_name: string | null } | null;
}

// ---------- Eigene Schichten ----------

/** Aktuell laufende Schicht des eingeloggten Fahrers (status=open, ended_at=null) */
export function useMyOpenShift() {
  return useQuery({
    queryKey: ["my-open-shift"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return null;
      const { data } = await supabase
        .from("driver_shifts")
        .select("*")
        .eq("driver_id", u.user.id)
        .eq("status", "open")
        .is("ended_at", null)
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return (data as Shift | null) ?? null;
    },
    refetchInterval: 60_000,
  });
}

export function useMyShifts(limit = 50) {
  return useQuery({
    queryKey: ["my-shifts", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("driver_shifts")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as Shift[];
    },
  });
}

export function useStartShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ lat, lng }: { lat?: number; lng?: number }) => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("nicht eingeloggt");
      const { data, error } = await supabase
        .from("driver_shifts")
        .insert({
          driver_id: u.user.id,
          started_at: new Date().toISOString(),
          started_lat: lat ?? null,
          started_lng: lng ?? null,
          status: "open",
        })
        .select()
        .single();
      if (error) throw error;
      return data as Shift;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-open-shift"] });
      qc.invalidateQueries({ queryKey: ["my-shifts"] });
    },
  });
}

export function useEndShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ shiftId, lat, lng, breakMinutes, notes }: {
      shiftId: string; lat?: number; lng?: number; breakMinutes?: number; notes?: string;
    }) => {
      const { error } = await supabase
        .from("driver_shifts")
        .update({
          ended_at: new Date().toISOString(),
          ended_lat: lat ?? null,
          ended_lng: lng ?? null,
          break_minutes: breakMinutes ?? 0,
          notes: notes ?? null,
          status: "submitted",
        })
        .eq("id", shiftId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-open-shift"] });
      qc.invalidateQueries({ queryKey: ["my-shifts"] });
      qc.invalidateQueries({ queryKey: ["all-shifts"] });
    },
  });
}

// ---------- Backoffice ----------

export function useAllShifts(filter: { status?: ShiftStatus; driverId?: string } = {}) {
  return useQuery({
    queryKey: ["all-shifts", filter],
    queryFn: async () => {
      let q = supabase
        .from("driver_shifts")
        .select("*, driver:profiles!driver_shifts_driver_id_fkey(full_name)")
        .order("started_at", { ascending: false });
      if (filter.status) q = q.eq("status", filter.status);
      if (filter.driverId) q = q.eq("driver_id", filter.driverId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ShiftWithJoins[];
    },
  });
}

export function useApproveShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (shiftId: string) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("driver_shifts")
        .update({ status: "approved", approved_by: u.user?.id ?? null, approved_at: new Date().toISOString() })
        .eq("id", shiftId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all-shifts"] });
      qc.invalidateQueries({ queryKey: ["my-shifts"] });
    },
  });
}

export function useRejectShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("driver_shifts")
        .update({
          status: "rejected",
          approved_by: u.user?.id ?? null,
          approved_at: new Date().toISOString(),
          rejection_note: reason,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all-shifts"] });
    },
  });
}

// ---------- Helpers ----------

export function shiftDurationMinutes(s: Pick<Shift, "started_at" | "ended_at" | "break_minutes">): number {
  if (!s.ended_at) return 0;
  const ms = new Date(s.ended_at).getTime() - new Date(s.started_at).getTime();
  return Math.max(0, Math.round(ms / 60000) - (s.break_minutes ?? 0));
}

export const STATUS_SHIFT_LABEL: Record<ShiftStatus, string> = {
  open: "Läuft",
  submitted: "Zur Freigabe",
  approved: "Freigegeben",
  rejected: "Abgelehnt",
};
export const STATUS_SHIFT_PILL: Record<ShiftStatus, string> = {
  open: "pill bg-wiese-100 text-wiese-800",
  submitted: "pill bg-sonne-100 text-sonne-800",
  approved: "pill-success",
  rejected: "pill-danger",
};
